/**
 * liveDocLoadState — a tiny, React-free guard that marks when the live Word
 * document is being (re)loaded/hydrated and is therefore still "settling".
 *
 * WHY THIS EXISTS
 * ---------------
 * Loading a run inserts the whole .docx (or replays it section-by-section) and
 * then re-attaches native heading numbering + built-in styles (wordWriter
 * hydrate STEP3/STEP3.5). For a few seconds after the content first appears the
 * document is NOT settled: ``paragraph.styleBuiltIn`` / ``listItem.listString``
 * on the leading headings are still being rewritten by Word web.
 *
 * The regulatory-audit re-projection (``reviewStore.reprojectSection`` →
 * ``reinsertModuleIntoLiveDoc`` → ``insertSectionContent``) is a
 * delete-then-reinsert. If it runs during that settling window its next-heading
 * scan is degraded (later headings aren't recognized yet), so its deletion
 * boundary can fall through to end-of-document and wipe a large region of the
 * live doc. The server bundle is untouched, so a browser refresh restores
 * everything — the exact "blanks a few seconds after Load" symptom.
 *
 * ``pauseSync()`` only suspends the read-only auto-save watcher; it does NOT
 * cover re-projection. This module is the shared signal that lets any body-
 * mutating background work (currently: re-projection) stand down until the load
 * has settled.
 *
 * Reference-counted on purpose: the shell brackets the *whole* load+settle
 * window while the low-level loaders (``hydrateLiveDocFromBase64`` /
 * ``loadRunIntoLiveDoc``) also bracket their own insert. Nested begins keep the
 * flag set until the outermost end; the deferred queue drains once — when the
 * count returns to zero.
 */

let _loadDepth = 0;

/**
 * Bumped whenever the live document body is rebuilt from scratch (a load /
 * hydrate begins, the scaffold clears the body, Reset). A long-running write —
 * ``insertSectionContent`` renders one node per sync — captures it up front and
 * stops as soon as it changes: its anchor paragraphs belong to a body that no
 * longer exists, and further inserts could land anywhere (the "part of a section
 * above the title page" bug).
 */
let _docEpoch = 0;

/** Current live-document epoch — see {@link bumpDocEpoch}. */
export function currentDocEpoch(): number {
	return _docEpoch;
}

/** Record that the live document body was (or is about to be) replaced. */
export function bumpDocEpoch(): void {
	_docEpoch += 1;
}

/** Deferred jobs keyed so repeat requests for the same section coalesce — the
 *  latest re-projection request wins. */
const _pending = new Map<string, () => void>();

/** Mark that a live-doc load/hydrate has started. Balance every call with
 *  {@link endLiveDocLoad} (use try/finally). */
export function beginLiveDocLoad(): void {
	_loadDepth += 1;
	bumpDocEpoch();
}

/** Mark that a live-doc load/hydrate has finished. When the outermost load
 *  ends, any work deferred via {@link deferUntilLiveDocSettled} runs now. */
export function endLiveDocLoad(): void {
	if (_loadDepth === 0) return; // unbalanced end — ignore rather than go negative
	_loadDepth -= 1;
	if (_loadDepth > 0 || _pending.size === 0) return;
	const jobs = Array.from(_pending.values());
	_pending.clear();
	for (const job of jobs) {
		try {
			job();
		} catch (err) {
			console.warn("[liveDocLoadState] deferred job threw (non-fatal):", err);
		}
	}
}

/** True while a live-doc load/hydrate is in flight (and, per the shell's
 *  bracketing, while the freshly loaded doc is still settling). */
export function isLiveDocLoading(): boolean {
	return _loadDepth > 0;
}

/**
 * Run ``fn`` immediately when the live doc is not loading; otherwise defer it
 * until the current load(s) settle. Deduped by ``key`` — a later defer with the
 * same key replaces the earlier pending job.
 */
export function deferUntilLiveDocSettled(key: string, fn: () => void): void {
	if (!isLiveDocLoading()) {
		fn();
		return;
	}
	_pending.set(key, fn);
}

/** Test hook — reset the counter and pending queue. */
export function __resetLiveDocLoadState(): void {
	_loadDepth = 0;
	_pending.clear();
}
